import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

interface AudioReactiveCanvasProps {
  audioLevel: number;
  isSpeaking: boolean;
  conversation?: any;
  width?: number;
  height?: number;
  miniMode?: boolean;
}

const AudioReactiveCanvas: React.FC<AudioReactiveCanvasProps> = ({
  audioLevel,
  isSpeaking,
  conversation,
  width = 144,
  height = 144,
  miniMode = false,
}) => {
  const [realtimeLevel, setRealtimeLevel] = useState(audioLevel);

  useEffect(() => {
    let animationFrame: number;

    const updateAudioLevel = () => {
      if (conversation) {
        try {
          let inputLevel = 0;
          let outputLevel = 0;

          // Get input audio (user speaking)
          if (typeof conversation.getInputByteFrequencyData === 'function') {
            const inputData = conversation.getInputByteFrequencyData();
            if (inputData && inputData.length > 0) {
              const midRange = inputData.slice(10, 50);
              const average =
                midRange.reduce(
                  (sum: number, value: number) => sum + value,
                  0,
                ) / midRange.length;
              inputLevel = Math.min(average / 80, 1.5);
            }
          }

          // Get output audio (AI speaking)
          if (typeof conversation.getOutputByteFrequencyData === 'function') {
            const outputData = conversation.getOutputByteFrequencyData();
            if (outputData && outputData.length > 0) {
              const midRange = outputData.slice(10, 50);
              const average =
                midRange.reduce(
                  (sum: number, value: number) => sum + value,
                  0,
                ) / midRange.length;
              outputLevel = Math.min(average / 80, 1.5);
            }
          }

          // Use the maximum of input and output levels
          const maxLevel = Math.max(inputLevel, outputLevel);
          setRealtimeLevel(maxLevel);
        } catch (error) {
          setRealtimeLevel(audioLevel);
        }
      } else {
        setRealtimeLevel(audioLevel);
      }

      animationFrame = requestAnimationFrame(updateAudioLevel);
    };

    updateAudioLevel();

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [audioLevel, conversation]);

  const pulseIntensity = isSpeaking ? realtimeLevel : 0.3;
  const scale = 0.6 + pulseIntensity * 0.3; // Reduced from 0.5 + 0.5 to 0.6 + 0.3
  const opacity = 0.7 + pulseIntensity * 0.3;
  const glowScale = 0.8 + pulseIntensity * 0.4; // Reduced from 0.6 to 0.4
  const shadowBlur = 10 + pulseIntensity * 20; // Reduced from 30 to 20

  return (
    <div
      style={{
        width,
        height,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Outer glow - reduced in mini mode */}
      {!miniMode && (
        <motion.div
          animate={{
            scale: glowScale,
            opacity: opacity * 0.8,
          }}
          transition={{
            type: 'spring',
            stiffness: 500,
            damping: 20,
            duration: 0.1,
          }}
          style={{
            position: 'absolute',
            width: '150%',
            height: '150%',
            borderRadius: '50%',
            background: `radial-gradient(circle, rgba(151, 213, 252, ${opacity * 0.8}) 0%, rgba(151, 213, 252, ${opacity * 0.4}) 40%, transparent 70%)`,
          }}
        />
      )}

      {/* Main star */}
      <motion.div
        animate={{
          scale: scale,
          rotate: isSpeaking && realtimeLevel > 0.1 ? [0, 360] : 0,
        }}
        transition={{
          scale: {
            type: 'spring',
            stiffness: 600,
            damping: 15,
            duration: 0.05,
          },
          rotate: {
            duration: 2,
            repeat: isSpeaking && realtimeLevel > 0.1 ? Infinity : 0,
            ease: 'linear',
          },
        }}
        style={{
          width: miniMode ? '200%' : '50%',
          height: miniMode ? '200%' : '50%',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg
          width='100%'
          height='100%'
          viewBox='0 0 80 80'
          style={{
            filter: `drop-shadow(0 0 ${miniMode ? shadowBlur * 0.5 : shadowBlur}px ${isSpeaking ? '#FFFEFC' : '#97D5FC'})`,
          }}
        >
          <motion.path
            d='M40 5 L47 32 L75 40 L47 48 L40 75 L33 48 L5 40 L33 32 Z'
            fill={isSpeaking ? '#FFFEFC' : '#97D5FC'}
            animate={{
              opacity: opacity,
              scale: 1 + realtimeLevel * 0.15,
            }}
            transition={{
              type: 'spring',
              stiffness: 800,
              damping: 25,
              duration: 0.05,
            }}
            style={{
              transformOrigin: '40px 40px', // Center the rotation
            }}
          />
        </svg>
      </motion.div>
    </div>
  );
};

export default AudioReactiveCanvas;
